import { EOptionSymbolSide, ESymbolCategory } from '@aqx-core/common/api/market';

import { IOptionNameDisplay, IOptionSymbolMonthSide } from './types';

// ---------------------
// Example usage:
// ---------------------
// console.log(parseSymbolToDisplayName("CBQK511F00003194E-1@DSMD"));
// => "CBQK 20250611 3.194 CALL (-1)"

export const mapSymbolName = (symbol: string | null | undefined, symbolType: ESymbolCategory | undefined | null) => {
  if (!symbol) {
    return '';
  }
  if (symbolType !== ESymbolCategory.OPTION || !symbolType) {
    return symbol;
  }
  return parseSymbolToDisplayName(symbol);
};

// --------------- private function helper --------------- //

/**
 * Maps single-letter month+type codes to { month, symbolSide }.
 * A–L = CALL (Jan–Dec), M–X = PUT (Jan–Dec)
 */
const parseMonthType = (char: string): IOptionSymbolMonthSide => {
  const monthMappings: Record<string, IOptionSymbolMonthSide> = {
    // Calls (Jan-Dec)
    A: { month: 1, symbolSide: EOptionSymbolSide.CALL },
    B: { month: 2, symbolSide: EOptionSymbolSide.CALL },
    C: { month: 3, symbolSide: EOptionSymbolSide.CALL },
    D: { month: 4, symbolSide: EOptionSymbolSide.CALL },
    E: { month: 5, symbolSide: EOptionSymbolSide.CALL },
    F: { month: 6, symbolSide: EOptionSymbolSide.CALL },
    G: { month: 7, symbolSide: EOptionSymbolSide.CALL },
    H: { month: 8, symbolSide: EOptionSymbolSide.CALL },
    I: { month: 9, symbolSide: EOptionSymbolSide.CALL },
    J: { month: 10, symbolSide: EOptionSymbolSide.CALL },
    K: { month: 11, symbolSide: EOptionSymbolSide.CALL },
    L: { month: 12, symbolSide: EOptionSymbolSide.CALL },
    // Puts (Jan-Dec)
    M: { month: 1, symbolSide: EOptionSymbolSide.PUT },
    N: { month: 2, symbolSide: EOptionSymbolSide.PUT },
    O: { month: 3, symbolSide: EOptionSymbolSide.PUT },
    P: { month: 4, symbolSide: EOptionSymbolSide.PUT },
    Q: { month: 5, symbolSide: EOptionSymbolSide.PUT },
    R: { month: 6, symbolSide: EOptionSymbolSide.PUT },
    S: { month: 7, symbolSide: EOptionSymbolSide.PUT },
    T: { month: 8, symbolSide: EOptionSymbolSide.PUT },
    U: { month: 9, symbolSide: EOptionSymbolSide.PUT },
    V: { month: 10, symbolSide: EOptionSymbolSide.PUT },
    W: { month: 11, symbolSide: EOptionSymbolSide.PUT },
    X: { month: 12, symbolSide: EOptionSymbolSide.PUT }
  };

  const result = monthMappings[char];
  if (!result) {
    throw new Error(`Invalid month/type code: ${char}`);
  }

  return result;
};

/**
 * Interprets a single digit (0–9) as a future year, based on the current decade.
 *
 * Example logic:
 * - Get the current decade, e.g. if now is 2030–2039, decade = 2030.
 * - Then year = decade + digit. (So if digit=0, year=2030, digit=1 => 2031, etc.)
 */
const parseYearDigit = (digit: number): number => {
  const currentYear = new Date().getFullYear();
  const currentDecade = Math.floor(currentYear / 10) * 10; // e.g. 2030
  const year = currentDecade + digit;
  return year;
};

/**
 * Format the date , e.g. 2025-06-11 => "11062025"
 */
const formatDate = (yearDigit: string, day: string, month: number): string => {
  const year = parseYearDigit(parseInt(yearDigit, 10));
  const monthStr = month.toString().padStart(2, '0');
  const dayStr = parseInt(day, 10).toString().padStart(2, '0');
  return `${dayStr}${monthStr}${year}`;
};

/**
 * Format the strike price , e.g. 00003 => 3, plus decimal e.g. 194 => .194 => "3.194"
 */
const formatStrike = (whole: string, decimal: string): string => {
  if (!whole && !decimal) {
    return '';
  }
  const wholeNum = parseInt(whole, 10);
  const decimalNum = parseInt(decimal, 10);
  return `${wholeNum}.${String(decimalNum).padStart(3, '0')}`;
};

/**
 * Extract the components from the option symbol
 */
const extractOptionComponents = (symbol: string): IOptionNameDisplay => {
  // 1. Strip any trailing "@..." part
  const [mainPart] = symbol.split('@');
  // 2. Check if there's a suffix that starts with "-", this is coorporate action -1 , -2 , -3 etc
  const dashIndex = mainPart.indexOf('-');
  const suffix = dashIndex !== -1 ? mainPart.slice(dashIndex) : '';
  const trimmedPart = dashIndex !== -1 ? mainPart.slice(0, dashIndex) : mainPart;

  // The last 13 characters of trimmedPart hold the standard QSE Option structure
  // 1 char year + 2 char day + 1 char monthType + 5 char strikeWhole + 3 char strikeDecimal + 1 char style
  const optionSpec = trimmedPart.slice(-13);
  return {
    symbol: trimmedPart.slice(0, trimmedPart.length - 13), // Extract the symbol name by taking all characters before the 13-char option specification
    yearDigit: optionSpec[0],
    day: optionSpec.slice(1, 3),
    monthType: optionSpec[3],
    strikeWhole: optionSpec.slice(4, 9),
    strikeDecimal: optionSpec.slice(9, 12),
    style: optionSpec[12],
    suffix
  };
};

/**
 * Parse the symbol to display name
 */
export const parseSymbolToDisplayName = (symbol: string): string => {
  const components = extractOptionComponents(symbol);
  const { month, symbolSide } = parseMonthType(components.monthType);
  // Format components
  const dateString = formatDate(components.yearDigit, components.day, month);
  const strikeDisplay = formatStrike(components.strikeWhole, components.strikeDecimal);
  const suffixDisplay = components.suffix ? `(${components.suffix})` : '';

  const parts = [components.symbol.trim(), dateString, strikeDisplay, symbolSide, suffixDisplay].filter(Boolean);
  return parts.join(' ');
};
